import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import {
  SectionAdminBar,
  useBlockConfig,
  ArrayDeleteButton,
  AddItemButton,
} from "./shared";

interface ContactInfoBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function ContactInfoBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: ContactInfoBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const heading = get("heading", "");
  const subheading = get("subheading", "");
  const intro = get("intro", "");
  const infoCards = get("infoCards", []) as {
    id: string;
    icon: string;
    iconBg: string;
    iconColor: string;
    title: string;
    lines: string[];
    visible: boolean;
  }[];
  const socialLinks = get("socialLinks", []) as { id: string; icon: string; label: string; href: string }[];

  const setInfoCard = (idx: number, key: string, val: unknown) => {
    const newCards = infoCards.map((c, i) => (i === idx ? { ...c, [key]: val } : c));
    setConfig("infoCards", newCards);
  };

  const deleteInfoCard = (idx: number) => {
    const visibleCards = infoCards.filter((c) => c.visible !== false);
    const cardToHide = visibleCards[idx];
    if (!cardToHide) return;
    const fullIdx = infoCards.findIndex((c) => c.id === cardToHide.id);
    if (fullIdx < 0) return;
    const newCards = infoCards.map((c, i) => (i === fullIdx ? { ...c, visible: false } : c));
    setConfig("infoCards", newCards);
  };

  const setSocial = (idx: number, key: "icon" | "label" | "href", val: string) => {
    const newLinks = socialLinks.map((l, i) => (i === idx ? { ...l, [key]: val } : l));
    setConfig("socialLinks", newLinks);
  };

  const addSocialLink = () => {
    setConfig("socialLinks", [...socialLinks, { id: `social-${Date.now()}`, icon: "ri-instagram-line", label: "Instagram", href: "#" }]);
  };

  const deleteSocialLink = (idx: number) => {
    setConfig("socialLinks", socialLinks.filter((_, i) => i !== idx));
  };

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="contact-info"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-12 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff" }}>
        <div className="text-center mb-8">
          {subheading && (
            <AdminEditField value={subheading} onSave={(v) => setConfig("subheading", v)} inputClassName="text-center text-sm">
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{subheading}</span>
            </AdminEditField>
          )}
          {heading && (
            <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-center text-3xl md:text-4xl font-extrabold">
              <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {heading}
              </h2>
            </AdminEditField>
          )}
          {intro && (
            <AdminEditField value={intro} onSave={(v) => setConfig("intro", v)} inputClassName="text-center">
              <p className={`text-base max-w-xl mx-auto ${subColor}`}>{intro}</p>
            </AdminEditField>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          {infoCards.filter((c) => c.visible !== false).map((card, idx) => (
            <div
              key={card.id}
              className={`rounded-2xl p-5 text-center border relative group/infocard ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
              }`}
            >
              {isAdmin && onUpdate && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteInfoCard(idx); }}
                  className="absolute -top-2 -right-2 z-10 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/infocard:opacity-100"
                  title="Hide info card"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
              <div className={`w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-3 ${card.iconBg}`}>
                {isAdmin && onUpdate ? (
                  <div className="flex flex-col items-center">
                    <i className={`${card.icon} ${card.iconColor} text-xl`}></i>
                    <AdminEditField
                      value={card.icon}
                      onSave={(v) => setInfoCard(idx, "icon", v)}
                      inputClassName="text-[8px] w-full text-center"
                    >
                      <span className="text-[8px] text-gray-400">{card.icon}</span>
                    </AdminEditField>
                  </div>
                ) : (
                  <i className={`${card.icon} ${card.iconColor} text-xl`}></i>
                )}
              </div>
              <AdminEditField
                value={card.title}
                onSave={(v) => setInfoCard(idx, "title", v)}
                inputClassName="font-bold text-sm mb-2"
              >
                <h4 className={`font-bold text-sm mb-2 ${headingColor}`}>{card.title}</h4>
              </AdminEditField>
              {card.lines.map((line, li) => (
                <p key={li} className={`text-sm ${subColor}`}>
                  {isAdmin && onUpdate ? (
                    <AdminEditField
                      value={line}
                      onSave={(v) => {
                        const newLines = card.lines.map((l, i) => (i === li ? v : l));
                        setInfoCard(idx, "lines", newLines);
                      }}
                      inputClassName="text-sm"
                    >
                      {line}
                    </AdminEditField>
                  ) : (
                    line
                  )}
                </p>
              ))}
            </div>
          ))}
        </div>

        {socialLinks.length > 0 && (
          <div className="flex justify-center gap-4 flex-wrap">
            {socialLinks.map((link, idx) => (
              <div key={link.id} className="relative group/social">
                {isAdmin && onUpdate && (
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSocialLink(idx); }}
                    className="absolute -top-2 -right-2 z-10 w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/social:opacity-100"
                  >
                    <i className="ri-close-line" style={{ fontSize: "8px" }}></i>
                  </button>
                )}
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                    isDark
                      ? "bg-gray-700 text-gray-300 hover:bg-pink-500 hover:text-white"
                      : "bg-pink-50 text-pink-500 hover:bg-pink-500 hover:text-white"
                  }`}
                >
                  {isAdmin && onUpdate ? (
                    <div className="flex flex-col items-center">
                      <i className={`${link.icon} text-lg`}></i>
                      <AdminEditField
                        value={link.icon}
                        onSave={(v) => setSocial(idx, "icon", v)}
                        inputClassName="text-[8px] w-full text-center"
                      >
                        <span className="text-[8px] text-gray-400">{link.icon}</span>
                      </AdminEditField>
                    </div>
                  ) : (
                    <i className={`${link.icon} text-lg`}></i>
                  )}
                </a>
              </div>
            ))}
            {isAdmin && onUpdate && (
              <button
                onClick={(e) => { e.stopPropagation(); addSocialLink(); }}
                className={`w-11 h-11 flex items-center justify-center rounded-full border-2 border-dashed cursor-pointer transition-colors ${
                  isDark
                    ? "border-gray-600 text-gray-400 hover:border-pink-400 hover:text-pink-400"
                    : "border-gray-300 text-gray-400 hover:border-pink-400 hover:text-pink-500"
                }`}
              >
                <i className="ri-add-line"></i>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}